<template>
  <div class="wizard-container">

    <!-- ── STEPPER ──────────────────────────────────────── -->
    <div class="wizard-stepper-wrap">
      <div class="wizard-steps">
        <div
          v-for="(step, i) in steps"
          :key="i"
          class="wizard-step"
          :class="{
            'is-active':    pasoActual === i,
            'is-completed': pasoActual > i
          }"
          @click="irAPaso(i)"
        >
          <div class="step-bubble">
            <i v-if="pasoActual > i" class="pi pi-check"></i>
            <span v-else>{{ i + 1 }}</span>
          </div>
          <span class="step-label">{{ step }}</span>
          <div v-if="i < steps.length - 1" class="step-connector"></div>
        </div>
      </div>
    </div>

    <!-- ── CUERPO ────────────────────────────────────────── -->
    <div class="wizard-body">

      <!-- ════════════ PASO 1 — INFO DEL VIAJE ════════════ -->
      <div v-show="pasoActual === 0">

        <!-- Información General -->
        <div class="wiz-section">
          <div class="wiz-section-header">
            <i class="pi pi-info-circle"></i>
            <span>Información General</span>
          </div>
          <div class="grid p-fluid px-3 pt-3">

            <div class="col-12 lg:col-6">
              <span class="p-float-label">
                <InputText
                  id="wiz_email"
                  v-model="frmViaje.email"
                  type="email"
                  :class="{'p-invalid': errores.email}"
                  @blur="validarCampo('email')"
                  @input="limpiarError('email')"
                />
                <label for="wiz_email">Email conductor *</label>
              </span>
              <small class="p-error" v-if="errores.email">{{ errores.email }}</small>
            </div>

            <div class="col-12 lg:col-3">
              <span class="p-float-label">
                <Calendar
                  v-model="frmViaje.start_at"
                  :showButtonBar="true"
                  :showIcon="true"
                  dateFormat="yy-mm-dd"
                  :locale="$primevue.config.locale"
                  :class="{'p-invalid': errores.start_at}"
                  @date-select="limpiarError('start_at')"
                />
                <label>Fecha Inicio *</label>
              </span>
              <small class="p-error" v-if="errores.start_at">{{ errores.start_at }}</small>
            </div>

            <div class="col-12 lg:col-3">
              <span class="p-float-label">
                <Calendar
                  v-model="frmViaje.finish_at"
                  :showButtonBar="true"
                  :showIcon="true"
                  dateFormat="yy-mm-dd"
                  :locale="$primevue.config.locale"
                  :class="{'p-invalid': errores.finish_at}"
                  @date-select="limpiarError('finish_at')"
                />
                <label>Fecha Fin *</label>
              </span>
              <small class="p-error" v-if="errores.finish_at">{{ errores.finish_at }}</small>
            </div>

            <div class="col-12 lg:col-6">
              <span class="p-float-label">
                <Dropdown
                  v-model="frmViaje.tipo_despacho"
                  :options="tipos_despachos"
                  optionLabel="description"
                  optionValue="code_type_dispatch"
                  :class="{'p-invalid': errores.tipo_despacho}"
                  @change="limpiarError('tipo_despacho')"
                />
                <label>Tipo de Despacho *</label>
              </span>
              <small class="p-error" v-if="errores.tipo_despacho">{{ errores.tipo_despacho }}</small>
            </div>

            <div class="col-12 lg:col-6">
              <span class="p-float-label">
                <InputText v-model="frmViaje.branch_description" />
                <label>Sucursal</label>
              </span>
            </div>

            <div class="col-12 lg:col-6">
              <span class="p-float-label">
                <InputText
                  v-model="frmViaje.reference_document"
                  type="text"
                  :class="{'p-invalid': errores.reference_document}"
                  @blur="validarCampo('reference_document')"
                  @input="limpiarError('reference_document')"
                />
                <label>Documento Referencia *</label>
              </span>
              <small class="p-error" v-if="errores.reference_document">{{ errores.reference_document }}</small>
            </div>

            <div class="col-12 lg:col-6 pb-3">
              <span class="p-float-label">
                <InputText v-model="frmViaje.ref_externa" />
                <label>Referencia Externa</label>
              </span>
            </div>

          </div>
        </div>

        <!-- Representante y Propietario -->
        <div class="wiz-section">
          <div class="wiz-section-header">
            <i class="pi pi-users"></i>
            <span>Representante y Propietario</span>
          </div>
          <div class="grid p-fluid px-3 pt-3">
            <div class="col-12 lg:col-3">
              <span class="p-float-label">
                <InputText
                  v-model="frmViaje.representative_id"
                  type="number"
                  :class="{'p-invalid': errores.representative_id}"
                  @blur="validarCampo('representative_id')"
                  @input="limpiarError('representative_id')"
                />
                <label>Doc. Representante *</label>
              </span>
              <small class="p-error" v-if="errores.representative_id">{{ errores.representative_id }}</small>
            </div>
            <div class="col-12 lg:col-3">
              <span class="p-float-label">
                <InputText
                  v-model="frmViaje.representative"
                  :class="{'p-invalid': errores.representative}"
                  @blur="validarCampo('representative')"
                  @input="limpiarError('representative')"
                />
                <label>Nombre Representante *</label>
              </span>
              <small class="p-error" v-if="errores.representative">{{ errores.representative }}</small>
            </div>
            <div class="col-12 lg:col-3">
              <span class="p-float-label">
                <InputText
                  v-model="frmViaje.owner_id"
                  type="number"
                  :class="{'p-invalid': errores.owner_id}"
                  @blur="validarCampo('owner_id')"
                  @input="limpiarError('owner_id')"
                />
                <label>Doc. Propietario *</label>
              </span>
              <small class="p-error" v-if="errores.owner_id">{{ errores.owner_id }}</small>
            </div>
            <div class="col-12 lg:col-3 pb-3">
              <span class="p-float-label">
                <InputText
                  v-model="frmViaje.owner"
                  :class="{'p-invalid': errores.owner}"
                  @blur="validarCampo('owner')"
                  @input="limpiarError('owner')"
                />
                <label>Nombre Propietario *</label>
              </span>
              <small class="p-error" v-if="errores.owner">{{ errores.owner }}</small>
            </div>
          </div>
        </div>

        <!-- Parámetros -->
        <div class="wiz-section">
          <div class="wiz-section-header">
            <i class="pi pi-cog"></i>
            <span>Parámetros de Validación</span>
          </div>
          <div class="grid p-fluid px-3 pt-3">
            <div class="col-12 lg:col-4">
              <span class="p-float-label">
                <InputNumber
                  v-model="frmViaje.valor_viaje"
                  mode="currency" currency="COP" locale="es-CO" :min="1"
                  :class="{'p-invalid': errores.valor_viaje}"
                  @blur="validarCampo('valor_viaje')"
                />
                <label>Costo Total del Viaje *</label>
              </span>
              <small class="p-error" v-if="errores.valor_viaje">{{ errores.valor_viaje }}</small>
            </div>
            <div class="col-12 lg:col-4">
              <span class="p-float-label">
                <InputText v-model="frmViaje.hours_validation" type="number" />
                <label>Vigencia PreOp. (Horas)</label>
              </span>
            </div>
            <div class="col-12 lg:col-4 pb-3">
              <div class="wiz-toggle-row">
                <div>
                  <div class="wiz-toggle-label">PreOperacional requerido</div>
                </div>
                <InputSwitch v-model="frmViaje.required_check" />
              </div>
            </div>
          </div>
        </div>

        <div class="wizard-nav-row">
          <span></span>
          <Button label="Vehículo →" class="button-sewayblue" @click="irAlSiguiente" />
        </div>
      </div>
      <!-- /PASO 1 -->


      <!-- ════════════ PASO 2 — VEHÍCULO ════════════ -->
      <div v-show="pasoActual === 1">

        <div class="wiz-section">
          <div class="wiz-section-header">
            <i class="pi pi-car"></i>
            <span>Identificación del Vehículo</span>
          </div>
          <div class="grid p-fluid px-3 pt-3">

            <div class="col-12 lg:col-6">
              <span class="p-float-label">
                <InputMask
                  mask="aaa-999"
                  v-model="frmVehiculo.placa"
                  :class="{'p-invalid': erroresV.placa}"
                  @blur="validarCampoV('placa'); buscarVehiculoMaestro()"
                  @input="limpiarErrorV('placa')"
                />
                <label>Placa Camión *</label>
              </span>
              <small class="p-error" v-if="erroresV.placa">{{ erroresV.placa }}</small>
            </div>

            <div class="col-12 lg:col-6">
              <span class="p-float-label">
                <InputMask mask="a-99999" v-model="frmVehiculo.id_trailer" />
                <label>Placa Remolque</label>
              </span>
            </div>

            <!-- Banner de vehículo encontrado -->
            <div class="col-12" v-if="buscandoVehiculo">
              <div class="vehiculo-buscando">
                <i class="pi pi-spin pi-spinner mr-2"></i>
                Buscando vehículo registrado...
              </div>
            </div>

            <div class="col-12" v-if="vehiculoEncontrado && !buscandoVehiculo">
              <div class="vehiculo-encontrado-banner">
                <div class="vehiculo-encontrado-left">
                  <i class="pi pi-check-circle mr-2" style="color:#059669; font-size:1.1rem"></i>
                  <div>
                    <div class="vehiculo-encontrado-titulo">Vehículo encontrado en el historial</div>
                    <div class="vehiculo-encontrado-sub">
                      Usado <strong>{{ vehiculoEncontrado.veces_utilizado }}</strong> veces — datos completados automáticamente
                    </div>
                  </div>
                </div>
                <Button
                  icon="pi pi-times"
                  class="p-button-text p-button-sm p-button-secondary"
                  title="Cerrar"
                  @click="vehiculoEncontrado = null"
                />
              </div>
            </div>

          </div>
        </div>

        <div class="wiz-section">
          <div class="wiz-section-header">
            <i class="pi pi-cog"></i>
            <span>Características del Vehículo</span>
          </div>
          <div class="grid p-fluid px-3 pt-3">
            <div class="col-12 lg:col-3">
              <span class="p-float-label">
                <Dropdown v-model="frmVehiculo.vehicle_brand" :options="catalogo_marcas" optionLabel="descripcion" optionValue="descripcion" :editable="true" />
                <label>Marca</label>
              </span>
            </div>
            <div class="col-12 lg:col-3">
              <span class="p-float-label">
                <Dropdown v-model="frmVehiculo.vehicle_fuel_type" :options="catalogo_combustible" optionLabel="label" optionValue="value" />
                <label>Combustible</label>
              </span>
            </div>
            <div class="col-12 lg:col-3">
              <span class="p-float-label">
                <Dropdown v-model="frmVehiculo.vehicle_configuration" :options="catalogo_configuracion" optionLabel="nombre" optionValue="nombre" />
                <label>Configuración</label>
              </span>
            </div>
            <div class="col-12 lg:col-3 pb-3">
              <span class="p-float-label">
                <Dropdown v-model="frmVehiculo.vehicle_class" :options="catalogo_configuracion" optionLabel="descripcion" optionValue="descripcion" />
                <label>Clase</label>
              </span>
            </div>
          </div>
        </div>


        <div class="wizard-nav-row">
          <Button label="← Info del Viaje" class="p-button-outlined text-orange" @click="irAPaso(0)" />
          <Button label="Detalles →" class="button-sewayblue" @click="irAlSiguiente" />
        </div>
      </div>
      <!-- /PASO 2 -->


      <!-- ════════════ PASO 3 — DETALLES ════════════ -->
      <div v-show="pasoActual === 2">

        <div class="wiz-section-header-detalles">
          <div class="flex align-items-center gap-2">
            <i class="pi pi-box"></i>
            <span class="ml-1">Detalles de Entrega</span>
            <Badge :value="detalles_viaje.length" severity="info" class="ml-1" />
          </div>
          <Button label="Agregar Detalle" icon="pi pi-plus" class="p-button-success" style="width:auto;" @click="agregarDetalle" />
        </div>

        <Message v-if="submitted_final && detalles_viaje.length === 0" severity="error" :closable="false">
          <i class="pi pi-exclamation-triangle mr-2"></i>
          Debe agregar al menos un detalle de entrega.
        </Message>
        <Message v-else-if="detalles_viaje.length === 0" severity="warn" :closable="false">
          <i class="pi pi-info-circle mr-2"></i>
          Haga clic en "Agregar Detalle" para comenzar.
        </Message>

        <Accordion :multiple="true" :activeIndex="activeDetalles" class="detalles-wizard-accordion">
          <AccordionTab v-for="(detalle, idx) in detalles_viaje" :key="idx">

            <template #header>
              <div class="detalle-tab-header">

                <!-- Número con estado -->
                <span class="detalle-badge" :class="tieneErroresDetalle(idx) ? 'error' : ''">
                  <i v-if="tieneErroresDetalle(idx)" class="pi pi-exclamation-triangle" style="font-size:0.65rem"></i>
                  <span v-else>#{{ idx + 1 }}</span>
                </span>

                <!-- Info principal -->
                <div class="detalle-header-info">

                  <!-- Fila 1: cliente y producto -->
                  <div class="detalle-header-top">
                    <span class="detalle-cliente">
                      <i class="pi pi-user" style="font-size:0.75rem; margin-right:3px; opacity:0.6"></i>
                      {{ detalle.nombre_cliente || 'Sin cliente' }}
                    </span>
                    <span v-if="detalle.producto" class="detalle-producto-pill">
                      <i class="pi pi-box" style="font-size:0.65rem; margin-right:3px"></i>
                      {{ detalle.producto }}
                    </span>
                  </div>

                  <!-- Fila 2: ruta, peso, unidades -->
                  <div class="detalle-header-bottom">
                    <span v-if="detalle.ciudad_origen || detalle.ciudad_destino" class="detalle-ruta-item">
                      <i class="pi pi-map-marker" style="font-size:0.65rem; color:#10b981"></i>
                      <span class="detalle-ciudad-origen">{{ detalle.ciudad_origen || '—' }}</span>
                      <i class="pi pi-arrow-right" style="font-size:0.55rem; margin:0 4px; opacity:0.5"></i>
                      <i class="pi pi-map-marker" style="font-size:0.65rem; color:#E53935"></i>
                      <span class="detalle-ciudad-destino">{{ detalle.ciudad_destino || '—' }}</span>
                    </span>
                    <span v-if="detalle.peso" class="detalle-meta-chip">
                      <i class="pi pi-chart-bar" style="font-size:0.6rem"></i>
                      {{ new Intl.NumberFormat('es-CO').format(detalle.peso) }} kg
                    </span>
                    <span v-if="detalle.unidades" class="detalle-meta-chip">
                      <i class="pi pi-list" style="font-size:0.6rem"></i>
                      {{ detalle.unidades }} uds
                    </span>
                  </div>

                </div>

                <!-- Geocercas -->
                <div class="detalle-header-geo" v-if="detalle.geo_send_type !== 'none' || detalle.geo_received_type !== 'none'">
                  <span v-if="detalle.geo_send_type !== 'none'" class="geo-chip verde"
                    :title="'Geocerca remitente: ' + detalle.geo_send_type">
                    R {{ detalle.geo_send_type === 'circular' ? '⊙' : '⬡' }}
                  </span>
                  <span v-if="detalle.geo_received_type !== 'none'" class="geo-chip naranja"
                    :title="'Geocerca destinatario: ' + detalle.geo_received_type">
                    D {{ detalle.geo_received_type === 'circular' ? '⊙' : '⬡' }}
                  </span>
                </div>

                <!-- Botón eliminar -->
                <Button
                  icon="pi pi-trash"
                  class="p-button-rounded p-button-danger p-button-text p-button-sm detalle-btn-eliminar"
                  @click.stop="eliminarDetalle(idx)"
                  title="Eliminar detalle"
                />

              </div>
            </template>

            <!-- ORIGEN / DESTINO — antes era solo el header suelto -->
            <div class="wiz-section mb-3">
              <div class="wiz-section-header">
                <i class="pi pi-map-marker"></i>
                <span>Origen y Destino (DIVIPOLA)</span>
              </div>
              <div class="grid p-fluid px-3 pt-3 pb-2">
                <div class="col-12 lg:col-3">
                  <span class="p-float-label">
                    <Dropdown v-model="detalle.departamento_origen" :options="departamentosUnicos" optionLabel="label" optionValue="value" :filter="true" :class="{'p-invalid': detalle.submitted && !detalle.departamento_origen}" @change="detalle.ciudad_origen = ''" />
                    <label>Dpto. Origen *</label>
                  </span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.departamento_origen">Requerido</small>
                </div>
                <div class="col-12 lg:col-3">
                  <span class="p-float-label">
                    <Dropdown v-model="detalle.ciudad_origen" :options="municipiosPorDepartamento(detalle.departamento_origen)" optionLabel="label" optionValue="value" :filter="true" :disabled="!detalle.departamento_origen" :class="{'p-invalid': detalle.submitted && !detalle.ciudad_origen}" />
                    <label>Ciudad Origen *</label>
                  </span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.ciudad_origen">Requerido</small>
                </div>
                <div class="col-12 lg:col-3">
                  <span class="p-float-label">
                    <Dropdown v-model="detalle.departamento_destino" :options="departamentosUnicos" optionLabel="label" optionValue="value" :filter="true" :class="{'p-invalid': detalle.submitted && !detalle.departamento_destino}" @change="detalle.ciudad_destino = ''" />
                    <label>Dpto. Destino *</label>
                  </span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.departamento_destino">Requerido</small>
                </div>
                <div class="col-12 lg:col-3">
                  <span class="p-float-label">
                    <Dropdown v-model="detalle.ciudad_destino" :options="municipiosPorDepartamento(detalle.departamento_destino)" optionLabel="label" optionValue="value" :filter="true" :disabled="!detalle.departamento_destino" :class="{'p-invalid': detalle.submitted && !detalle.ciudad_destino}" />
                    <label>Ciudad Destino *</label>
                  </span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.ciudad_destino">Requerido</small>
                </div>
                <div class="col-12 lg:col-3 pb-2">
                  <span class="p-float-label"><Calendar v-model="detalle.load_estimated" :showTime="true" :showIcon="true" dateFormat="yy-mm-dd" :locale="$primevue.config.locale" /><label>Fecha Cita Cargue</label></span>
                </div>
                <div class="col-12 lg:col-3 pb-2">
                  <span class="p-float-label"><Calendar v-model="detalle.unload_estimated" :showTime="true" :showIcon="true" dateFormat="yy-mm-dd" :locale="$primevue.config.locale" /><label>Fecha Cita Descargue</label></span>
                </div>
              </div>
            </div>

            <!-- PRODUCTO -->
            <div class="wiz-section mb-3">
              <div class="wiz-section-header">
                <i class="pi pi-box"></i>
                <span>Producto / Carga</span>
              </div>
              <div class="grid p-fluid px-3 pt-3 pb-2">
                <div class="col-12 lg:col-4">
                  <span class="p-float-label">
                    <InputText v-model="detalle.producto" :class="{'p-invalid': detalle.submitted && !detalle.producto}" />
                    <label>Producto *</label>
                  </span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.producto">Requerido</small>
                </div>
                <div class="col-12 lg:col-2"><span class="p-float-label"><InputText v-model="detalle.empaque" /><label>Empaque</label></span></div>
                <div class="col-12 lg:col-2">
                  <span class="p-float-label">
                    <InputNumber v-model="detalle.peso" suffix=" kg" :min="0" :maxFractionDigits="2" :class="{'p-invalid': detalle.submitted && !detalle.peso}" />
                    <label>Peso (kg) *</label>
                  </span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.peso">Requerido</small>
                </div>
                <div class="col-12 lg:col-2">
                  <span class="p-float-label">
                    <InputNumber v-model="detalle.unidades" :min="1" :maxFractionDigits="0" :class="{'p-invalid': detalle.submitted && !detalle.unidades}" />
                    <label>Unidades *</label>
                  </span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.unidades">Requerido</small>
                </div>
                <div class="col-12 lg:col-2"><span class="p-float-label"><InputText v-model="detalle.contenedor" /><label>Contenedor</label></span></div>
                <div class="col-12 lg:col-3"><span class="p-float-label"><InputText v-model="detalle.client_document1" /><label>Documento 1</label></span></div>
                <div class="col-12 lg:col-3"><span class="p-float-label"><InputText v-model="detalle.client_document2" /><label>Documento 2</label></span></div>
                <div class="col-12 lg:col-3"><span class="p-float-label"><InputText v-model="detalle.client_document3" /><label>Documento 3</label></span></div>
                <div class="col-12 lg:col-3 pb-2"><span class="p-float-label"><InputText v-model="detalle.client_document4" /><label>Documento 4</label></span></div>
              </div>
            </div>

            <!-- CLIENTE -->
            <div class="wiz-section mb-3">
              <div class="wiz-section-header">
                <i class="pi pi-user"></i>
                <span>Cliente</span>
              </div>
              <div class="grid p-fluid px-3 pt-3 pb-2">
                <div class="col-12 lg:col-3">
                  <span class="p-float-label">
                    <InputText v-model="detalle.id_cliente" type="number" :class="{'p-invalid': detalle.submitted && !detalle.id_cliente}" />
                    <label>ID Cliente *</label>
                  </span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.id_cliente">Requerido</small>
                </div>
                <div class="col-12 lg:col-5">
                  <span class="p-float-label">
                    <InputText v-model="detalle.nombre_cliente" :class="{'p-invalid': detalle.submitted && !detalle.nombre_cliente}" />
                    <label>Nombre / Razón Social *</label>
                  </span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.nombre_cliente">Requerido</small>
                </div>
                <div class="col-12 lg:col-4"><span class="p-float-label"><InputText v-model="detalle.client_address" /><label>Dirección</label></span></div>
                <div class="col-12 lg:col-3"><span class="p-float-label"><InputText v-model="detalle.client_phone" /><label>Teléfono</label></span></div>
                <div class="col-12 lg:col-3"><span class="p-float-label"><InputText v-model="detalle.client_mobile" /><label>Celular</label></span></div>
                <div class="col-12 lg:col-6"><span class="p-float-label"><InputText v-model="detalle.client_email" type="email" /><label>Email Cliente</label></span></div>
                <div class="col-12 lg:col-3">
                  <span class="p-float-label"><Dropdown v-model="detalle.departamento_cliente" :options="departamentosUnicos" optionLabel="label" optionValue="value" :filter="true" @change="detalle.ciudad_cliente = ''" /><label>Dpto. Cliente</label></span>
                </div>
                <div class="col-12 lg:col-3 pb-2">
                  <span class="p-float-label"><Dropdown v-model="detalle.ciudad_cliente" :options="municipiosPorDepartamento(detalle.departamento_cliente)" optionLabel="label" optionValue="value" :filter="true" :disabled="!detalle.departamento_cliente" /><label>Ciudad Cliente</label></span>
                </div>
              </div>
            </div>

            <!-- REMITENTE -->
            <div class="wiz-section mb-3">
              <div class="wiz-section-header wiz-section-header--verde">
                <i class="pi pi-share-alt"></i>
                <span>Remitente</span>
              </div>
              <div class="grid p-fluid px-3 pt-3 pb-2">
                <div class="col-12 lg:col-2"><span class="p-float-label"><InputText v-model="detalle.send_code" /><label>Código</label></span></div>
                <div class="col-12 lg:col-3">
                  <span class="p-float-label"><InputText v-model="detalle.send_id" type="number" :class="{'p-invalid': detalle.submitted && !detalle.send_id}" /><label>NIT / ID *</label></span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.send_id">Requerido</small>
                </div>
                <div class="col-12 lg:col-7">
                  <span class="p-float-label"><InputText v-model="detalle.send_name" :class="{'p-invalid': detalle.submitted && !detalle.send_name}" /><label>Razón Social *</label></span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.send_name">Requerido</small>
                </div>
                <div class="col-12 lg:col-6">
                  <span class="p-float-label"><InputText v-model="detalle.send_address" :class="{'p-invalid': detalle.submitted && !detalle.send_address}" /><label>Dirección *</label></span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.send_address">Requerido</small>
                </div>
                <div class="col-12 lg:col-3"><span class="p-float-label"><InputText v-model="detalle.send_phone" /><label>Teléfono</label></span></div>
                <div class="col-12 lg:col-3"><span class="p-float-label"><InputText v-model="detalle.send_mobile" /><label>Celular</label></span></div>
                <div class="col-12 lg:col-6"><span class="p-float-label"><InputText v-model="detalle.send_email" type="email" /><label>Email</label></span></div>
                <div class="col-12 lg:col-3">
                  <span class="p-float-label"><Dropdown v-model="detalle.send_country" :options="departamentosUnicos" optionLabel="label" optionValue="value" :filter="true" :class="{'p-invalid': detalle.submitted && !detalle.send_country}" @change="detalle.send_city = ''" /><label>Departamento *</label></span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.send_country">Requerido</small>
                </div>
                <div class="col-12 lg:col-3">
                  <span class="p-float-label"><Dropdown v-model="detalle.send_city" :options="municipiosPorDepartamento(detalle.send_country)" optionLabel="label" optionValue="value" :filter="true" :disabled="!detalle.send_country" :class="{'p-invalid': detalle.submitted && !detalle.send_city}" /><label>Ciudad *</label></span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.send_city">Requerido</small>
                </div>
                <div class="col-12 lg:col-3"><span class="p-float-label"><InputText v-model="detalle.send_latitude" /><label>Latitud</label></span></div>
                <div class="col-12 lg:col-3"><span class="p-float-label"><InputText v-model="detalle.send_longitude" /><label>Longitud</label></span></div>

                <!-- GEOCERCA REMITENTE — sin cambios -->
                <div class="col-12 pb-2">
                  <div class="geocerca-card verde">
                    <!-- ... igual que antes ... -->
                    <div class="geocerca-card-header"><i class="pi pi-map-marker mr-1"></i><span>Geocerca Remitente</span></div>
                    <div class="geocerca-selector">
                      <div v-for="opt in geoOpciones" :key="opt.value" class="geocerca-option" :class="{ selected: detalle.geo_send_type === opt.value }" @click="seleccionarGeo(idx,'send',opt.value)">
                        <span class="geo-icon">{{ opt.icon }}</span><span>{{ opt.label }}</span>
                      </div>
                    </div>
                    <div v-if="detalle.geo_send_type !== 'none'" class="geocerca-mapa-wrap">
                      <div class="geo-search-row">
                        <InputText v-model="detalle.geo_send_search" placeholder="Buscar dirección…" class="geo-search-input" @keydown.enter.prevent="buscarDireccionGeo(idx,'send')" />
                        <Button icon="pi pi-search" class="p-button-outlined p-button-sm" :loading="detalle.geo_send_searching" @click="buscarDireccionGeo(idx,'send')" />
                      </div>
                      <div :id="`map-send-${idx}`" class="geo-leaflet-map" style="height:500px;width:100%;border-radius:6px;margin-top:0.5rem;"></div>
                      <div v-if="detalle.geo_send_type === 'circular'" class="geo-controles-circular">
                        <div class="grid p-fluid">
                          <div class="col-12 lg:col-4"><span class="p-float-label"><InputText v-model="detalle.geo_send_lat" readonly class="geo-coord-input" /><label>Latitud Centro</label></span></div>
                          <div class="col-12 lg:col-4"><span class="p-float-label"><InputText v-model="detalle.geo_send_lng" readonly class="geo-coord-input" /><label>Longitud Centro</label></span></div>
                          <div class="col-12 lg:col-4"><span class="p-float-label"><InputNumber v-model="detalle.geo_send_radius" suffix=" m" :min="10" :max="50000" @input="actualizarCirculo(idx,'send')" /><label>Radio</label></span></div>
                          <div class="col-12">
                            <div class="geo-slider-row">
                              <span class="geo-slider-min">10 m</span>
                              <Slider v-model="detalle.geo_send_radius" :min="10" :max="5000" class="flex-1" @change="actualizarCirculo(idx,'send')" />
                              <span class="geo-slider-max">5 km</span>
                              <span class="geo-slider-badge">{{ detalle.geo_send_radius >= 1000 ? (detalle.geo_send_radius/1000).toFixed(1)+' km' : detalle.geo_send_radius+' m' }}</span>
                            </div>
                          </div>
                        </div>
                        <div class="flex justify-content-end mt-2"><Button icon="pi pi-times" label="Limpiar geocerca" class="p-button-text p-button-sm p-button-danger" @click="limpiarGeo(idx,'send')" /></div>
                      </div>
                      <div v-if="detalle.geo_send_type === 'polygonal'" class="geo-controles-poly">
                        <div class="flex justify-content-between align-items-center mb-2">
                          <small class="text-500">Haz clic en el mapa para agregar vértices (mín. 3)</small>
                          <Button icon="pi pi-times" label="Limpiar" class="p-button-text p-button-sm p-button-danger" @click="limpiarGeo(idx,'send')" />
                        </div>
                        <DataTable :value="detalle.geo_send_poly" class="p-datatable-sm geo-poly-table" :rows="10">
                          <template #empty><div class="text-center text-500 py-2 text-sm">Sin puntos. Haz clic en el mapa.</div></template>
                          <Column header="#" style="width:40px"><template #body="s"><span class="geo-poly-num">{{ s.index+1 }}</span></template></Column>
                          <Column field="lat" header="Latitud"><template #body="s"><code>{{ parseFloat(s.data.lat).toFixed(6) }}</code></template></Column>
                          <Column field="lng" header="Longitud"><template #body="s"><code>{{ parseFloat(s.data.lng).toFixed(6) }}</code></template></Column>
                          <Column style="width:50px"><template #body="s"><Button icon="pi pi-times" class="p-button-text p-button-danger p-button-sm" @click="eliminarPuntoPoly(idx,'send',s.index)" /></template></Column>
                        </DataTable>
                        <small v-if="detalle.geo_send_poly.length > 0" class="geo-poly-count mt-1">
                          <strong>{{ detalle.geo_send_poly.length }} puntos</strong>
                          <span v-if="detalle.geo_send_poly.length >= 3" class="text-green-600"> · Polígono válido ✓</span>
                          <span v-else class="text-orange-500"> · Se necesitan al menos 3</span>
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- DESTINATARIO -->
            <div class="wiz-section mb-3">
              <div class="wiz-section-header wiz-section-header--rojo">
                <i class="pi pi-building"></i>
                <span>Destinatario</span>
              </div>
              <div class="grid p-fluid px-3 pt-3 pb-2">
                <div class="col-12 lg:col-2"><span class="p-float-label"><InputText v-model="detalle.received_code" /><label>Código</label></span></div>
                <div class="col-12 lg:col-3">
                  <span class="p-float-label"><InputText v-model="detalle.received_id" type="number" :class="{'p-invalid': detalle.submitted && !detalle.received_id}" /><label>NIT / ID *</label></span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.received_id">Requerido</small>
                </div>
                <div class="col-12 lg:col-7">
                  <span class="p-float-label"><InputText v-model="detalle.received_name" :class="{'p-invalid': detalle.submitted && !detalle.received_name}" /><label>Razón Social *</label></span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.received_name">Requerido</small>
                </div>
                <div class="col-12 lg:col-6">
                  <span class="p-float-label"><InputText v-model="detalle.received_address" :class="{'p-invalid': detalle.submitted && !detalle.received_address}" /><label>Dirección *</label></span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.received_address">Requerido</small>
                </div>
                <div class="col-12 lg:col-3"><span class="p-float-label"><InputText v-model="detalle.received_phone" /><label>Teléfono</label></span></div>
                <div class="col-12 lg:col-3"><span class="p-float-label"><InputText v-model="detalle.received_mobile" /><label>Celular</label></span></div>
                <div class="col-12 lg:col-6"><span class="p-float-label"><InputText v-model="detalle.received_email" type="email" /><label>Email</label></span></div>
                <div class="col-12 lg:col-3">
                  <span class="p-float-label"><Dropdown v-model="detalle.received_country" :options="departamentosUnicos" optionLabel="label" optionValue="value" :filter="true" :class="{'p-invalid': detalle.submitted && !detalle.received_country}" @change="detalle.received_city = ''" /><label>Departamento *</label></span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.received_country">Requerido</small>
                </div>
                <div class="col-12 lg:col-3">
                  <span class="p-float-label"><Dropdown v-model="detalle.received_city" :options="municipiosPorDepartamento(detalle.received_country)" optionLabel="label" optionValue="value" :filter="true" :disabled="!detalle.received_country" :class="{'p-invalid': detalle.submitted && !detalle.received_city}" /><label>Ciudad *</label></span>
                  <small class="p-error" v-if="detalle.submitted && !detalle.received_city">Requerido</small>
                </div>
                <div class="col-12 lg:col-3"><span class="p-float-label"><InputText v-model="detalle.receive_latitude" /><label>Latitud</label></span></div>
                <div class="col-12 lg:col-3"><span class="p-float-label"><InputText v-model="detalle.receive_longitude" /><label>Longitud</label></span></div>

                <!-- GEOCERCA DESTINATARIO — sin cambios -->
                <div class="col-12 pb-2">
                  <div class="geocerca-card rojo">
                    <div class="geocerca-card-header"><i class="pi pi-map-marker mr-1"></i><span>Geocerca Destinatario</span></div>
                    <div class="geocerca-selector">
                      <div v-for="opt in geoOpciones" :key="opt.value" class="geocerca-option" :class="{ selected: detalle.geo_received_type === opt.value }" @click="seleccionarGeo(idx,'received',opt.value)">
                        <span class="geo-icon">{{ opt.icon }}</span><span>{{ opt.label }}</span>
                      </div>
                    </div>
                    <div v-if="detalle.geo_received_type !== 'none'" class="geocerca-mapa-wrap">
                      <div class="geo-search-row">
                        <InputText v-model="detalle.geo_received_search" placeholder="Buscar dirección…" class="geo-search-input" @keydown.enter.prevent="buscarDireccionGeo(idx,'received')" />
                        <Button icon="pi pi-search" class="p-button-outlined p-button-sm" :loading="detalle.geo_received_searching" @click="buscarDireccionGeo(idx,'received')" />
                      </div>
                      <div :id="`map-received-${idx}`" class="geo-leaflet-map" style="height:500px;width:100%;border-radius:6px;margin-top:0.5rem;"></div>
                      <div v-if="detalle.geo_received_type === 'circular'" class="geo-controles-circular">
                        <div class="grid p-fluid">
                          <div class="col-12 lg:col-4"><span class="p-float-label"><InputText v-model="detalle.geo_received_lat" readonly class="geo-coord-input" /><label>Latitud Centro</label></span></div>
                          <div class="col-12 lg:col-4"><span class="p-float-label"><InputText v-model="detalle.geo_received_lng" readonly class="geo-coord-input" /><label>Longitud Centro</label></span></div>
                          <div class="col-12 lg:col-4"><span class="p-float-label"><InputNumber v-model="detalle.geo_received_radius" suffix=" m" :min="10" :max="50000" @input="actualizarCirculo(idx,'received')" /><label>Radio</label></span></div>
                          <div class="col-12">
                            <div class="geo-slider-row">
                              <span class="geo-slider-min">10 m</span>
                              <Slider v-model="detalle.geo_received_radius" :min="10" :max="5000" class="flex-1" @change="actualizarCirculo(idx,'received')" />
                              <span class="geo-slider-max">5 km</span>
                              <span class="geo-slider-badge">{{ detalle.geo_received_radius >= 1000 ? (detalle.geo_received_radius/1000).toFixed(1)+' km' : detalle.geo_received_radius+' m' }}</span>
                            </div>
                          </div>
                        </div>
                        <div class="flex justify-content-end mt-2"><Button icon="pi pi-times" label="Limpiar geocerca" class="p-button-text p-button-sm p-button-danger" @click="limpiarGeo(idx,'received')" /></div>
                      </div>
                      <div v-if="detalle.geo_received_type === 'polygonal'" class="geo-controles-poly">
                        <div class="flex justify-content-between align-items-center mb-2">
                          <small class="text-500">Haz clic en el mapa para agregar vértices (mín. 3)</small>
                          <Button icon="pi pi-times" label="Limpiar" class="p-button-text p-button-sm p-button-danger" @click="limpiarGeo(idx,'received')" />
                        </div>
                        <DataTable :value="detalle.geo_received_poly" class="p-datatable-sm geo-poly-table" :rows="10">
                          <template #empty><div class="text-center text-500 py-2 text-sm">Sin puntos. Haz clic en el mapa.</div></template>
                          <Column header="#" style="width:40px"><template #body="s"><span class="geo-poly-num">{{ s.index+1 }}</span></template></Column>
                          <Column field="lat" header="Latitud"><template #body="s"><code>{{ parseFloat(s.data.lat).toFixed(6) }}</code></template></Column>
                          <Column field="lng" header="Longitud"><template #body="s"><code>{{ parseFloat(s.data.lng).toFixed(6) }}</code></template></Column>
                          <Column style="width:50px"><template #body="s"><Button icon="pi pi-times" class="p-button-text p-button-danger p-button-sm" @click="eliminarPuntoPoly(idx,'received',s.index)" /></template></Column>
                        </DataTable>
                        <small v-if="detalle.geo_received_poly.length > 0" class="geo-poly-count mt-1">
                          <strong>{{ detalle.geo_received_poly.length }} puntos</strong>
                          <span v-if="detalle.geo_received_poly.length >= 3" class="text-green-600"> · Polígono válido ✓</span>
                          <span v-else class="text-orange-500"> · Se necesitan al menos 3</span>
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </AccordionTab>
        </Accordion>

        <!-- Nav paso 3 -->
        <div class="wizard-nav-row mt-3">
          <Button label="← Vehículo" class="p-button-outlined text-orange" @click="irAPaso(1)" />
          <Button label="Guardar Viaje" icon="pi pi-save" class="button-sewayblue" :loading="loader" @click="guardarViaje" />
        </div>
      </div>
      <!-- /PASO 3 -->

    </div>
  </div>
</template>

<script>
// ── SIN import de leaflet ──────────────────────────────────────
// El CDN de Mapbox GL JS ya fue cargado en index.html, por lo que
// window.mapboxgl está disponible globalmente.
 
export default {
  name: 'ModalCrearViaje3Pasos',
 
  props: {
    modelValue:             { type: Boolean, default: false },
    tipos_despachos:        { type: Array,   default: () => [] },
    divipola:               { type: Array,   default: () => [] },
    catalogo_marcas:        { type: Array,   default: () => [] },
    catalogo_configuracion: { type: Array,   default: () => [] },
    loader:                 { type: Boolean, default: false },
    mapbox_token:           { type: String,  default: '' },   // ← NUEVO PROP
  },
 
  emits: ['update:modelValue', 'viaje-guardado'],
 
  data() {
    return {
      pasoActual: 0,
      pasoMaxAlcanzado: 0,
      steps: ['Info del Viaje', 'Vehículo', 'Detalles'],
      catalogo_combustible: [
        { label: 'Diesel',       value: 'Diesel'       },
        { label: 'Gasolina',     value: 'Gasolina'     },
        { label: 'Gas Natural',  value: 'Gas Natural'  },
        { label: 'Eléctrico',    value: 'Eléctrico'    },
        { label: 'Híbrido',      value: 'Híbrido'      },
      ],
      catalogo_clase: [
        { label: 'Liviano',  value: 'Liviano'  },
        { label: 'Mediano',  value: 'Mediano'  },
        { label: 'Pesado',   value: 'Pesado'   },
        { label: 'Especial', value: 'Especial' },
      ],
      geoOpciones: [
        { label: 'Sin geocerca', value: 'none',      icon: '⊘' },
        { label: 'Circular',    value: 'circular',   icon: '◎' },
        { label: 'Poligonal',   value: 'polygonal',  icon: '⬡' },
      ],
 
      // geoMaps[key] = {
      //   map          : mapboxgl.Map,
      //   markers      : mapboxgl.Marker[],   (marcadores arrastables)
      //   sourceIds    : string[],            (IDs de fuentes/layers añadidos)
      //   clickHandler : function,
      // }
      geoMaps: {},
 
      // ── Paso 1
      frmViaje: {
        email: '', start_at: null, finish_at: null,
        tipo_despacho: null, branch_description: '',
        reference_document: '', ref_externa: '',
        representative_id: '', representative: '',
        owner_id: '', owner: '',
        valor_viaje: null, hours_validation: 24, required_check: false,
      },
      errores: {},
 
      // ── Paso 2
      frmVehiculo: {
        placa: '', id_trailer: '', conductor_email: '',
        vehicle_brand: '', vehicle_fuel_type: '',
        vehicle_configuration: '', vehicle_class: '',
      },
      erroresV: {},
 
      // ── Paso 3
      detalles_viaje: [],
      activeDetalles: [],
      submitted_final: false,
      buscandoVehiculo: false,
      vehiculoEncontrado: null,
    };
  },
 
  computed: {
    departamentosUnicos() {
      if (!this.divipola?.length) return [];
      return [...new Set(this.divipola.map(d => d.departamento))].sort()
        .map(d => ({ label: d, value: d }));
    },
    municipiosPorDepartamento() {
      return (dep) => {
        if (!dep || !this.divipola?.length) return [];
        return this.divipola.filter(d => d.departamento === dep)
          .map(d => ({ label: d.municipio, value: d.municipio }))
          .sort((a, b) => a.label.localeCompare(b.label));
      };
    },
  },
 
  methods: {
 
    // ════════════════════════════════════════════════════════════
    // NAVEGACIÓN — sin cambios
    // ════════════════════════════════════════════════════════════
    irAPaso(i) {
      if (i > this.pasoMaxAlcanzado) {
        const nombres = ['Info del Viaje', 'Vehículo', 'Detalles'];
        this.$toast.add({ severity: 'warn', summary: 'Paso bloqueado', detail: `Complete "${nombres[this.pasoMaxAlcanzado]}" antes de "${nombres[i]}"`, life: 4000 });
        return;
      }
      this.pasoActual = i;
    },
 
    irAlSiguiente() {
      if (this.pasoActual === 0) { if (!this._validarPaso1()) return; if (this.pasoMaxAlcanzado < 1) this.pasoMaxAlcanzado = 1; }
      if (this.pasoActual === 1) { if (!this._validarPaso2()) return; if (this.pasoMaxAlcanzado < 2) this.pasoMaxAlcanzado = 2; }
      this.pasoActual++;
    },
 
    // ════════════════════════════════════════════════════════════
    // VALIDACIONES — sin cambios
    // ════════════════════════════════════════════════════════════
    _validarPaso1() {
      this.errores = {};
      let ok = true;
      const f = this.frmViaje;
      const rx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!f.email || !rx.test(f.email))                                         { this.errores.email              = 'Email inválido'; ok = false; }
      if (!f.start_at)                                                            { this.errores.start_at           = 'Requerido';     ok = false; }
      if (!f.finish_at)                                                           { this.errores.finish_at          = 'Requerido';     ok = false; }
      if (!f.tipo_despacho)                                                       { this.errores.tipo_despacho      = 'Requerido';     ok = false; }
      if (!f.reference_document || String(f.reference_document).trim() === '')   { this.errores.reference_document = 'Requerido';     ok = false; }
      if (!f.valor_viaje || f.valor_viaje <= 0)                                  { this.errores.valor_viaje        = 'Debe ser mayor a $0'; ok = false; }
      if (!f.representative_id || String(f.representative_id).trim() === '')     { this.errores.representative_id  = 'Requerido';     ok = false; }
      if (!f.representative || f.representative.trim() === '')                   { this.errores.representative     = 'Requerido';     ok = false; }
      if (!f.owner_id || String(f.owner_id).trim() === '')                       { this.errores.owner_id           = 'Requerido';     ok = false; }
      if (!f.owner || f.owner.trim() === '')                                     { this.errores.owner              = 'Requerido';     ok = false; }
      if (!ok) this.$toast.add({ severity: 'error', summary: 'Paso 1 incompleto', detail: 'Corrija los campos marcados en rojo.', life: 5000 });
      return ok;
    },
 
    _validarPaso2() {
      this.erroresV = {};
      if (!this.frmVehiculo.placa || this.frmVehiculo.placa.replace(/-/g, '').trim() === '') {
        this.erroresV.placa = 'La placa es requerida';
        this.$toast.add({ severity: 'error', summary: 'Paso 2 incompleto', detail: 'Ingrese la placa del vehículo.', life: 5000 });
        return false;
      }
      return true;
    },
 
    _validarDetalles() {
      if (this.detalles_viaje.length === 0) {
        this.submitted_final = true;
        this.$toast.add({ severity: 'error', summary: 'Sin detalles', detail: 'Debe agregar al menos un detalle de entrega.', life: 5000 });
        return false;
      }
      const requeridos = ['departamento_origen','ciudad_origen','departamento_destino','ciudad_destino','producto','peso','unidades','id_cliente','nombre_cliente','send_id','send_name','send_address','send_country','send_city','received_id','received_name','received_address','received_country','received_city'];
      let todosOk = true, primerConError = -1;
      this.detalles_viaje.forEach((d, idx) => {
        d.submitted = true;
        const faltante = requeridos.some(c => { const v = d[c]; return v === '' || v === null || v === undefined; });
        if (faltante && primerConError < 0) primerConError = idx;
        if (faltante) todosOk = false;
        if (d.geo_send_type     === 'polygonal' && d.geo_send_poly.length     < 3) { todosOk = false; this.$toast.add({ severity: 'warn', summary: `Detalle #${idx+1} — Geocerca Remitente`,    detail: 'La geocerca poligonal necesita al menos 3 puntos.', life: 5000 }); }
        if (d.geo_received_type === 'polygonal' && d.geo_received_poly.length < 3) { todosOk = false; this.$toast.add({ severity: 'warn', summary: `Detalle #${idx+1} — Geocerca Destinatario`, detail: 'La geocerca poligonal necesita al menos 3 puntos.', life: 5000 }); }
        if (d.geo_send_type     === 'circular'  && (!d.geo_send_lat     || !d.geo_send_lng))     { todosOk = false; this.$toast.add({ severity: 'warn', summary: `Detalle #${idx+1} — Geocerca Remitente`,    detail: 'Haga clic en el mapa para fijar el centro circular.', life: 5000 }); }
        if (d.geo_received_type === 'circular'  && (!d.geo_received_lat || !d.geo_received_lng)) { todosOk = false; this.$toast.add({ severity: 'warn', summary: `Detalle #${idx+1} — Geocerca Destinatario`, detail: 'Haga clic en el mapa para fijar el centro circular.', life: 5000 }); }
      });
      if (!todosOk) { this.$toast.add({ severity: 'error', summary: 'Detalles incompletos', detail: 'Revise los campos marcados en rojo.', life: 6000 }); if (primerConError >= 0) this.activeDetalles = [primerConError]; }
      return todosOk;
    },
 
    // ════════════════════════════════════════════════════════════
    // GUARDAR — sin cambios
    // ════════════════════════════════════════════════════════════
    guardarViaje() {
      if (!this._validarPaso1()) { this.pasoActual = 0; return; }
      if (!this._validarPaso2()) { this.pasoActual = 1; return; }
      if (!this._validarDetalles()) return;
      const detalles = this.detalles_viaje.map((d, i) => ({
        origin: d.ciudad_origen, origin_county: d.departamento_origen,
        destination: d.ciudad_destino, destination_county: d.departamento_destino,
        product: d.producto, packing: d.empaque,
        weight: d.peso, unit: d.unidades, container: d.contenedor,
        load_estimated:   d.load_estimated   ? new Date(d.load_estimated).toISOString().replace('T',' ').slice(0,19)  : '',
        unload_estimated: d.unload_estimated ? new Date(d.unload_estimated).toISOString().replace('T',' ').slice(0,19) : '',
        client_id: parseInt(d.id_cliente)||null, client_name: d.nombre_cliente,
        client_address: d.client_address, client_phone: d.client_phone, client_mobile: d.client_mobile, client_email: d.client_email,
        client_city: d.ciudad_cliente, client_country: d.departamento_cliente,
        client_document1: d.client_document1, client_document2: d.client_document2, client_document3: d.client_document3, client_document4: d.client_document4,
        send_code: d.send_code, send_id: parseInt(d.send_id)||null, send_name: d.send_name, send_address: d.send_address,
        send_phone: d.send_phone, send_mobile: d.send_mobile, send_email: d.send_email, send_city: d.send_city, send_country: d.send_country,
        send_latitude: d.send_latitude, send_longitude: d.send_longitude,
        received_code: d.received_code, received_id: parseInt(d.received_id)||null, received_name: d.received_name, received_address: d.received_address,
        received_phone: d.received_phone, received_mobile: d.received_mobile, received_email: d.received_email,
        received_city: d.received_city, received_country: d.received_country,
        received_latitude: d.receive_latitude, received_longitude: d.receive_longitude,
        delivery_secuence: i + 1,
        geofence_send:     this._buildGeofence(d, 'send'),
        geofence_received: this._buildGeofence(d, 'received'),
      }));
      this.$emit('viaje-guardado', { viaje: { ...this.frmViaje, ...this.frmVehiculo }, detalles });
    },
 
    // ════════════════════════════════════════════════════════════
    // RESET
    // ════════════════════════════════════════════════════════════
    resetWizard() {
      Object.keys(this.geoMaps).forEach(k => this._destruirMapaGeo_byKey(k));
      this.pasoActual = 0; this.pasoMaxAlcanzado = 0;
      this.detalles_viaje = []; this.activeDetalles = []; this.submitted_final = false;
      this.errores = {}; this.erroresV = {};
      this.frmViaje    = { email:'', start_at:null, finish_at:null, tipo_despacho:null, branch_description:'', reference_document:'', ref_externa:'', representative_id:'', representative:'', owner_id:'', owner:'', valor_viaje:null, hours_validation:24, required_check:false };
      this.frmVehiculo = { placa:'', id_trailer:'', conductor_email:'', vehicle_brand:'', vehicle_fuel_type:'', vehicle_configuration:'', vehicle_class:'' };
      this.buscandoVehiculo = false; this.vehiculoEncontrado = null;
    },
 
    // ════════════════════════════════════════════════════════════
    // HELPERS DE VALIDACIÓN — sin cambios
    // ════════════════════════════════════════════════════════════
    tieneErroresDetalle(idx) {
      const d = this.detalles_viaje[idx]; if (!d?.submitted) return false;
      const reqs = ['departamento_origen','ciudad_origen','departamento_destino','ciudad_destino','producto','peso','unidades','id_cliente','nombre_cliente','send_id','send_name','send_address','send_country','send_city','received_id','received_name','received_address','received_country','received_city'];
      return reqs.some(c => { const v=d[c]; return v===''||v===null||v===undefined; });
    },
    validarCampo(campo)  { const val = this.frmViaje[campo];    if (val===null||val===undefined||(typeof val==='string'&&val.trim()==='')) this.errores  = { ...this.errores,  [campo]: 'Campo requerido' }; },
    validarCampoV(campo) { const val = this.frmVehiculo[campo]; if (!val||val.trim()==='')                                               this.erroresV = { ...this.erroresV, [campo]: 'Campo requerido' }; },
    limpiarError(campo)  { const c={...this.errores};  delete c[campo]; this.errores  = c; },
    limpiarErrorV(campo) { const c={...this.erroresV}; delete c[campo]; this.erroresV = c; },
 
    // ════════════════════════════════════════════════════════════
    // VEHÍCULO — sin cambios
    // ════════════════════════════════════════════════════════════
    buscarVehiculoMaestro() {
      const placa = (this.frmVehiculo.placa||'').replace(/-/g,'').trim();
      if (placa.length < 6) return;
      const cookie = {}; document.cookie.split(';').forEach(el => { const [k,v]=el.split('='); cookie[k.trim()]=v; });
      this.buscandoVehiculo = true; this.vehiculoEncontrado = null;
      window.api.call('post', '/buscar_vehiculo_maestro', { placa, id_rol: cookie['IdRol'] })
        .then(res => {
          if (res.status===200 && res.data?.data) {
            const v = res.data.data;
            if (v.placa_remolque)         this.frmVehiculo.id_trailer           = v.placa_remolque;
            if (v.marca_vehiculo)         this.frmVehiculo.vehicle_brand         = v.marca_vehiculo;
            if (v.tipo_combustible)       this.frmVehiculo.vehicle_fuel_type     = v.tipo_combustible;
            if (v.configuracion_vehiculo) this.frmVehiculo.vehicle_configuration = v.configuracion_vehiculo;
            if (v.clase_vehiculo)         this.frmVehiculo.vehicle_class         = v.clase_vehiculo;
            this.vehiculoEncontrado = v;
            this.$toast.add({ severity:'success', summary:'Vehículo encontrado', detail:'Datos completados automáticamente', life:3500 });
          }
        })
        .catch(() => { this.vehiculoEncontrado = null; })
        .finally(() => { this.buscandoVehiculo = false; });
    },
 
    // ════════════════════════════════════════════════════════════
    // DETALLES
    // ════════════════════════════════════════════════════════════
    agregarDetalle() {
      const n = {
        secuencia: this.detalles_viaje.length+1, submitted: false,
        departamento_origen:'', ciudad_origen:'', departamento_destino:'', ciudad_destino:'',
        load_estimated:null, unload_estimated:null,
        producto:'', empaque:'', peso:null, unidades:null, contenedor:'',
        client_document1:'', client_document2:'', client_document3:'', client_document4:'',
        id_cliente:'', nombre_cliente:'', client_address:'', client_phone:'', client_mobile:'', client_email:'',
        departamento_cliente:'', ciudad_cliente:'',
        send_code:'', send_id:'', send_name:'', send_address:'', send_phone:'', send_mobile:'',
        send_email:'', send_country:'', send_city:'', send_latitude:'', send_longitude:'',
        received_code:'', received_id:'', received_name:'', received_address:'',
        received_phone:'', received_mobile:'', received_email:'',
        received_country:'', received_city:'', receive_latitude:'', receive_longitude:'',
        geo_send_type:'none',     geo_send_lat:'',     geo_send_lng:'',     geo_send_radius:100,     geo_send_poly:[],     geo_send_search:'',     geo_send_searching:false,
        geo_received_type:'none', geo_received_lat:'', geo_received_lng:'', geo_received_radius:100, geo_received_poly:[], geo_received_search:'', geo_received_searching:false,
      };
      this.detalles_viaje.push(n);
      this.activeDetalles = [this.detalles_viaje.length-1];
      this.$toast.add({ severity:'success', summary:'Detalle agregado', detail:`Detalle #${n.secuencia} listo para completar`, life:2500 });
    },
 
    eliminarDetalle(idx) {
      this._destruirMapaGeo(idx,'send'); this._destruirMapaGeo(idx,'received');
      this.detalles_viaje.splice(idx,1);
      this.detalles_viaje.forEach((d,i) => { d.secuencia=i+1; });
    },
 
    // ════════════════════════════════════════════════════════════
    // GEOCERCAS — MAPBOX GL JS
    // ════════════════════════════════════════════════════════════
 
    seleccionarGeo(idx, side, tipo) {
      const d = this.detalles_viaje[idx];
      if (side === 'send') d.geo_send_type = tipo; else d.geo_received_type = tipo;
      if (tipo === 'none') { this._destruirMapaGeo(idx, side); return; }
      this.$nextTick(() => this.$nextTick(() => this._inicializarMapaGeo(idx, side, tipo)));
    },
 
    // ── Inicializar mapa Mapbox para geocerca ─────────────────
    _inicializarMapaGeo(idx, side, tipo) {
      const containerId = `map-${side}-${idx}`;
      const div = document.getElementById(containerId);
      if (!div) return;
 
      // Destruir si ya existía
      this._destruirMapaGeo(idx, side);
 
      if (!this.mapbox_token) {
        this.$toast.add({ severity: 'error', summary: 'Sin token', detail: 'No se recibió el token de Mapbox.', life: 4000 });
        return;
      }
 
      window.mapboxgl.accessToken = this.mapbox_token;
 
      const map = new window.mapboxgl.Map({
        container: containerId,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-75.5658, 6.2476],   // Medellín por defecto
        zoom: 12,
        attributionControl: false,
      });
 
      map.addControl(new window.mapboxgl.NavigationControl(), 'top-right');
 
      const g = { map, markers: [], sourceIds: [], clickHandler: null };
      const key = `${side}-${idx}`;
      this.geoMaps[key] = g;
 
      map.on('load', () => {
        if (tipo === 'circular') {
          g.clickHandler = (e) => {
            const { lat, lng } = e.lngLat;
            const d = this.detalles_viaje[idx];
            if (side === 'send')     { d.geo_send_lat     = lat.toFixed(6); d.geo_send_lng     = lng.toFixed(6); }
            else                     { d.geo_received_lat = lat.toFixed(6); d.geo_received_lng = lng.toFixed(6); }
            this._redibujarCirculo(idx, side);
          };
        } else {
          g.clickHandler = (e) => {
            const { lat, lng } = e.lngLat;
            this._agregarPuntoPoligono(idx, side, lat, lng);
          };
        }
        map.on('click', g.clickHandler);
        setTimeout(() => map.resize(), 150);
      });
    },
 
    // ── Destruir mapa por idx+side ────────────────────────────
    _destruirMapaGeo(idx, side) {
      this._destruirMapaGeo_byKey(`${side}-${idx}`);
    },
 
    _destruirMapaGeo_byKey(key) {
      const g = this.geoMaps[key];
      if (!g) return;
      // Quitar markers
      g.markers.forEach(m => m.remove());
      g.markers = [];
      // Quitar sources y layers
      g.sourceIds.forEach(sid => {
        [`${sid}-fill`, `${sid}-border`, `${sid}-line`].forEach(lid => {
          if (g.map.getLayer(lid)) g.map.removeLayer(lid);
        });
        if (g.map.getSource(sid)) g.map.removeSource(sid);
      });
      g.sourceIds = [];
      g.map.remove();
      delete this.geoMaps[key];
    },
 
    // ── Redibujar círculo ─────────────────────────────────────
    _redibujarCirculo(idx, side) {
      const key = `${side}-${idx}`;
      const g   = this.geoMaps[key];
      if (!g || !g.map.loaded()) return;
 
      const d   = this.detalles_viaje[idx];
      const lat = parseFloat(side === 'send' ? d.geo_send_lat     : d.geo_received_lat);
      const lng = parseFloat(side === 'send' ? d.geo_send_lng     : d.geo_received_lng);
      const r   = (side === 'send' ? d.geo_send_radius : d.geo_received_radius) || 100;
      if (isNaN(lat) || isNaN(lng)) return;
 
      const color = side === 'send' ? '#1B8A4C' : '#E53935';
      const sid   = `geo-circ-${key}`;
 
      // Marcador draggable (reemplazar si ya existe)
      g.markers.forEach(m => m.remove()); g.markers = [];
      const el = document.createElement('div');
      el.innerHTML = `<div style="width:22px;height:22px;background:#0A354E;border:2.5px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;font-size:9px;color:#fff;font-weight:700;cursor:grab;">⊙</div>`;
      const marker = new window.mapboxgl.Marker({ element: el, draggable: true })
        .setLngLat([lng, lat])
        .addTo(g.map);
      marker.on('drag', () => {
        const pos = marker.getLngLat();
        const dd  = this.detalles_viaje[idx];
        if (side === 'send') { dd.geo_send_lat = pos.lat.toFixed(6); dd.geo_send_lng = pos.lng.toFixed(6); }
        else                 { dd.geo_received_lat = pos.lat.toFixed(6); dd.geo_received_lng = pos.lng.toFixed(6); }
        this._actualizarSourceCirculo(idx, side);
      });
      g.markers.push(marker);
 
      // GeoJSON del círculo (polígono de 64 puntos)
      const circulo = this._geoJsonCirculo(lat, lng, r);
 
      if (g.map.getSource(sid)) {
        // Actualizar datos si la fuente ya existe
        g.map.getSource(sid).setData(circulo);
      } else {
        g.map.addSource(sid, { type: 'geojson', data: circulo });
        g.map.addLayer({ id: `${sid}-fill`,   type: 'fill',   source: sid, paint: { 'fill-color': color, 'fill-opacity': 0.15 } });
        g.map.addLayer({ id: `${sid}-border`, type: 'line',   source: sid, paint: { 'line-color': color, 'line-width': 2 } });
        g.sourceIds.push(sid);
      }
 
      g.map.flyTo({ center: [lng, lat], zoom: Math.max(g.map.getZoom(), 14), duration: 600 });
    },
 
    // Actualiza solo los datos del source del círculo (al arrastrar)
    _actualizarSourceCirculo(idx, side) {
      const key = `${side}-${idx}`;
      const g   = this.geoMaps[key];
      if (!g) return;
      const d   = this.detalles_viaje[idx];
      const lat = parseFloat(side === 'send' ? d.geo_send_lat     : d.geo_received_lat);
      const lng = parseFloat(side === 'send' ? d.geo_send_lng     : d.geo_received_lng);
      const r   = side === 'send' ? d.geo_send_radius : d.geo_received_radius;
      if (isNaN(lat) || isNaN(lng)) return;
      const sid = `geo-circ-${key}`;
      if (g.map.getSource(sid)) g.map.getSource(sid).setData(this._geoJsonCirculo(lat, lng, r));
    },
 
    // Llamado desde el slider/input de radio
    actualizarCirculo(idx, side) {
      this._actualizarSourceCirculo(idx, side);
    },
 
    // Genera GeoJSON Feature de polígono circular (64 puntos)
    _geoJsonCirculo(lat, lng, radioM) {
      const puntos = [];
      for (let i = 0; i <= 64; i++) {
        const angulo = (i * 360) / 64;
        const rad    = (angulo * Math.PI) / 180;
        const dLat   = (radioM / 111320) * Math.cos(rad);
        const dLng   = (radioM / (111320 * Math.cos((lat * Math.PI) / 180))) * Math.sin(rad);
        puntos.push([lng + dLng, lat + dLat]);
      }
      return { type: 'Feature', geometry: { type: 'Polygon', coordinates: [puntos] } };
    },
 
    // ── Agregar punto al polígono ─────────────────────────────
    _agregarPuntoPoligono(idx, side, lat, lng) {
      const key = `${side}-${idx}`;
      const g   = this.geoMaps[key];
      const d   = this.detalles_viaje[idx];
      const arr = side === 'send' ? d.geo_send_poly : d.geo_received_poly;
      const i   = arr.length;
      arr.push({ lat, lng });
 
      const color = side === 'send' ? '#1B8A4C' : '#E53935';
      const el    = document.createElement('div');
      el.innerHTML = `<div style="width:20px;height:20px;background:${color};border:2px solid #fff;border-radius:50%;box-shadow:0 2px 5px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;font-size:9px;color:#fff;font-weight:700;cursor:grab;">${i+1}</div>`;
      const marker = new window.mapboxgl.Marker({ element: el, draggable: true })
        .setLngLat([lng, lat])
        .addTo(g.map);
      marker.on('drag', () => {
        const pos  = marker.getLngLat();
        arr[i]     = { lat: pos.lat, lng: pos.lng };
        this._redibujarPoligono(idx, side);
      });
      g.markers.push(marker);
      this._redibujarPoligono(idx, side);
    },
 
    // ── Redibujar polígono ────────────────────────────────────
    _redibujarPoligono(idx, side) {
      const key = `${side}-${idx}`;
      const g   = this.geoMaps[key];
      if (!g || !g.map.loaded()) return;
      const d   = this.detalles_viaje[idx];
      const arr = side === 'send' ? d.geo_send_poly : d.geo_received_poly;
      const color = side === 'send' ? '#1B8A4C' : '#E53935';
      const sid   = `geo-poly-${key}`;
      const coords = arr.map(p => [p.lng, p.lat]);
 
      if (arr.length < 2) {
        // Limpiar si no hay suficientes puntos
        [`${sid}-fill`, `${sid}-border`, `${sid}-line`].forEach(lid => { if (g.map.getLayer(lid)) g.map.removeLayer(lid); });
        if (g.map.getSource(sid)) g.map.removeSource(sid);
        g.sourceIds = g.sourceIds.filter(s => s !== sid);
        return;
      }
 
      if (arr.length >= 3) {
        // Polígono cerrado
        const cerrado = [...coords, coords[0]];
        const geojson = { type: 'Feature', geometry: { type: 'Polygon', coordinates: [cerrado] } };
        if (g.map.getSource(sid)) {
          g.map.getSource(sid).setData(geojson);
          // Asegurarse de que el layer line no esté (si antes era línea)
          if (g.map.getLayer(`${sid}-line`))   g.map.removeLayer(`${sid}-line`);
          if (!g.map.getLayer(`${sid}-fill`))   g.map.addLayer({ id:`${sid}-fill`,   type:'fill', source:sid, paint:{ 'fill-color':color, 'fill-opacity':0.15 } });
          if (!g.map.getLayer(`${sid}-border`)) g.map.addLayer({ id:`${sid}-border`, type:'line', source:sid, paint:{ 'line-color':color, 'line-width':2 } });
        } else {
          g.map.addSource(sid, { type:'geojson', data:geojson });
          g.map.addLayer({ id:`${sid}-fill`,   type:'fill', source:sid, paint:{ 'fill-color':color, 'fill-opacity':0.15 } });
          g.map.addLayer({ id:`${sid}-border`, type:'line', source:sid, paint:{ 'line-color':color, 'line-width':2.5 } });
          if (!g.sourceIds.includes(sid)) g.sourceIds.push(sid);
        }
      } else {
        // Solo línea (2 puntos)
        const geojson = { type: 'Feature', geometry: { type: 'LineString', coordinates: coords } };
        if (g.map.getSource(sid)) {
          g.map.getSource(sid).setData(geojson);
          if (g.map.getLayer(`${sid}-fill`))   g.map.removeLayer(`${sid}-fill`);
          if (g.map.getLayer(`${sid}-border`)) g.map.removeLayer(`${sid}-border`);
          if (!g.map.getLayer(`${sid}-line`))  g.map.addLayer({ id:`${sid}-line`, type:'line', source:sid, paint:{ 'line-color':color, 'line-width':2, 'line-dasharray':[2,2] } });
        } else {
          g.map.addSource(sid, { type:'geojson', data:geojson });
          g.map.addLayer({ id:`${sid}-line`, type:'line', source:sid, paint:{ 'line-color':color, 'line-width':2, 'line-dasharray':[2,2] } });
          if (!g.sourceIds.includes(sid)) g.sourceIds.push(sid);
        }
      }
    },
 
    // ── Eliminar punto del polígono ───────────────────────────
    eliminarPuntoPoly(idx, side, pIdx) {
      const key   = `${side}-${idx}`;
      const g     = this.geoMaps[key];
      const d     = this.detalles_viaje[idx];
      const arr   = side === 'send' ? d.geo_send_poly : d.geo_received_poly;
      const color = side === 'send' ? '#1B8A4C' : '#E53935';
 
      // Quitar el marcador visual
      if (g?.markers[pIdx]) { g.markers[pIdx].remove(); g.markers.splice(pIdx, 1); }
      arr.splice(pIdx, 1);
 
      // Re-numerar marcadores restantes
      g?.markers.forEach((m, i) => {
        const el = m.getElement();
        if (el) el.innerHTML = `<div style="width:20px;height:20px;background:${color};border:2px solid #fff;border-radius:50%;box-shadow:0 2px 5px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;font-size:9px;color:#fff;font-weight:700;cursor:grab;">${i+1}</div>`;
        // Re-enlazar el drag con el índice correcto
        m.off('drag');
        m.on('drag', () => {
          const pos = m.getLngLat();
          arr[i] = { lat: pos.lat, lng: pos.lng };
          this._redibujarPoligono(idx, side);
        });
      });
 
      this._redibujarPoligono(idx, side);
    },
 
    // ── Limpiar geocerca ──────────────────────────────────────
    limpiarGeo(idx, side) {
      const key = `${side}-${idx}`;
      const g   = this.geoMaps[key];
      if (g) {
        g.markers.forEach(m => m.remove()); g.markers = [];
        g.sourceIds.forEach(sid => {
          [`${sid}-fill`, `${sid}-border`, `${sid}-line`].forEach(lid => { if (g.map.getLayer(lid)) g.map.removeLayer(lid); });
          if (g.map.getSource(sid)) g.map.removeSource(sid);
        });
        g.sourceIds = [];
      }
      const d = this.detalles_viaje[idx];
      if (side === 'send')     { d.geo_send_lat='';     d.geo_send_lng='';     d.geo_send_radius=100;     d.geo_send_poly=[]; }
      else                     { d.geo_received_lat=''; d.geo_received_lng=''; d.geo_received_radius=100; d.geo_received_poly=[]; }
    },
 
    // ── Buscar dirección (Nominatim — sin cambios) ────────────
    async buscarDireccionGeo(idx, side) {
      const d     = this.detalles_viaje[idx];
      const query = side === 'send' ? d.geo_send_search : d.geo_received_search;
      if (!query?.trim()) return;
      if (side === 'send') d.geo_send_searching = true; else d.geo_received_searching = true;
      try {
        const res  = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`, { headers: { 'Accept-Language': 'es' } });
        const data = await res.json();
        if (data.length) {
          const lat = parseFloat(data[0].lat), lng = parseFloat(data[0].lon);
          const key = `${side}-${idx}`;
          const g   = this.geoMaps[key];
          if (g) {
            g.map.flyTo({ center: [lng, lat], zoom: 16, duration: 1200 });
            const tipo = side === 'send' ? d.geo_send_type : d.geo_received_type;
            if (tipo === 'circular') {
              if (side === 'send') { d.geo_send_lat = lat.toFixed(6); d.geo_send_lng = lng.toFixed(6); }
              else                 { d.geo_received_lat = lat.toFixed(6); d.geo_received_lng = lng.toFixed(6); }
              setTimeout(() => this._redibujarCirculo(idx, side), 400);
            } else if (tipo === 'polygonal') {
              setTimeout(() => this._agregarPuntoPoligono(idx, side, lat, lng), 400);
            }
          }
        } else {
          this.$toast.add({ severity: 'warn', summary: 'Sin resultados', detail: 'No se encontró la dirección', life: 3000 });
        }
      } catch {
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo buscar la dirección', life: 3000 });
      } finally {
        if (side === 'send') d.geo_send_searching = false; else d.geo_received_searching = false;
      }
    },
 
    // ── Build geofence payload — sin cambios ──────────────────
    _buildGeofence(d, side) {
      const tipo = side === 'send' ? d.geo_send_type : d.geo_received_type;
      if (tipo === 'none') return {};
      if (tipo === 'circular') {
        const lat = parseFloat(side === 'send' ? d.geo_send_lat     : d.geo_received_lat);
        const lng = parseFloat(side === 'send' ? d.geo_send_lng     : d.geo_received_lng);
        const r   = side === 'send' ? d.geo_send_radius : d.geo_received_radius;
        if (isNaN(lat) || isNaN(lng)) return {};
        return { type: 'circular', center: { lat, lng }, radius: r };
      }
      if (tipo === 'polygonal') {
        const pts = side === 'send' ? d.geo_send_poly : d.geo_received_poly;
        if (pts.length < 3) return {};
        return { type: 'polygonal', polygon: pts.map(p => ({ lat: +p.lat.toFixed(6), lng: +p.lng.toFixed(6) })) };
      }
      return {};
    },
 
    // ── poblarDesdeDuplicado — sin cambios ────────────────────
    poblarDesdeDuplicado(data) {
      const tipoDespachoEncontrado = this.tipos_despachos?.find(t => t.description?.toUpperCase() === data.dispatch_type_description?.toUpperCase());
      const codigoDespacho = tipoDespachoEncontrado?.code_type_dispatch ?? null;
      const pagoLimpio     = data.payment ? parseFloat(String(data.payment).replace(/\./g,'').replace(',','.')) : null;
      this.frmViaje    = { email: data.driver_email??'', start_at:null, finish_at:null, tipo_despacho:codigoDespacho, branch_description:data.branch_description??'', reference_document:'', ref_externa:'', representative_id:String(data.representative_id??''), representative:data.representative??'', owner_id:String(data.owner_id??''), owner:data.owner??'', valor_viaje:pagoLimpio, hours_validation:24, required_check:false };
      this.frmVehiculo = { placa:data.truck_id??'', id_trailer:'', conductor_email:data.driver_email??'', vehicle_brand:data.vehicle_brand??'', vehicle_fuel_type:data.vehicle_fuel_type??'', vehicle_configuration:data.vehicle_configuration??'', vehicle_class:data.vehicle_class??'' };
      if (data.delivery_details?.length) {
        this.detalles_viaje = data.delivery_details.map((det,idx) => ({
          secuencia:idx+1, submitted:false,
          departamento_origen:det.sender_country??'', ciudad_origen:det.sender_city??'', departamento_destino:det.receiver_country??'', ciudad_destino:det.receiver_city??'',
          load_estimated:null, unload_estimated:null,
          producto:det.product??'', empaque:det.packing??'', peso:det.weight??null, unidades:det.unit??null, contenedor:'',
          client_document1:'', client_document2:'', client_document3:'', client_document4:'',
          id_cliente:String(det.client_id??''), nombre_cliente:det.client_name??'', client_address:det.client_address??'', client_phone:det.client_phone??'', client_mobile:det.client_mobile??'', client_email:det.client_email??'', departamento_cliente:det.client_country??'', ciudad_cliente:det.client_city??'',
          send_code:'', send_id:String(det.sender_id??''), send_name:det.sender_name??'', send_address:det.sender_address??'', send_phone:det.sender_phone??'', send_mobile:det.sender_mobile??'', send_email:det.sender_email??'', send_country:det.sender_country??'', send_city:det.sender_city??'', send_latitude:String(det.sender_latitude??''), send_longitude:String(det.sender_longitude??''),
          received_code:'', received_id:String(det.receiver_id??''), received_name:det.receiver_name??'', received_address:det.receiver_address??'', received_phone:det.receiver_phone??'', received_mobile:det.receiver_mobile??'', received_email:det.receiver_email??'', received_country:det.receiver_country??'', received_city:det.receiver_city??'', receive_latitude:String(det.received_latitude??''), receive_longitude:String(det.received_longitude??''),
          geo_send_type:'none', geo_send_lat:'', geo_send_lng:'', geo_send_radius:100, geo_send_poly:[], geo_send_search:'', geo_send_searching:false,
          geo_received_type:'none', geo_received_lat:'', geo_received_lng:'', geo_received_radius:100, geo_received_poly:[], geo_received_search:'', geo_received_searching:false,
        }));
        this.activeDetalles = data.delivery_details.map((_,i)=>i);
      }
      this.pasoActual = 0; this.pasoMaxAlcanzado = 2;
      this.$toast.add({ severity:'info', summary:'Viaje duplicado', detail:'Datos pre-cargados. Completa la Referencia, fechas y revisa antes de guardar.', life:6000 });
    },
  },
};
</script>

<style scoped lang="scss">
.text-orange{
    background: white;
    color: #EB7D10;
	border: 1px solid #EB7D10;
}
.wizard-stepper-wrap {
  padding: 0.5rem 0 1rem;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 1.25rem;
}
.wizard-steps { display: flex; align-items: center; justify-content: center; gap: 0; }
.wizard-step {
  display: flex; align-items: center; gap: 0.5rem;
  cursor: pointer; padding: 0.3rem 0.6rem; border-radius: 6px; transition: background 0.2s;
  &:hover { background: #f0f4f8; }
  &.is-active    .step-bubble { background: #0A354E; color: #fff; }
  &.is-completed .step-bubble { background: #10b981; color: #fff; }
  &.is-active    .step-label  { color: #0A354E; font-weight: 700; }
  &.is-completed .step-label  { color: #10b981; }
}
.step-bubble { width:30px; height:30px; border-radius:50%; background:#e2e8f0; color:#64748b; display:flex; align-items:center; justify-content:center; font-size:.82rem; font-weight:700; flex-shrink:0; transition:background .2s,color .2s; }
.step-label  { font-size:.85rem; color:#64748b; white-space:nowrap; }
.step-connector { width:48px; height:2px; background:#e2e8f0; margin:0 .25rem; flex-shrink:0; }

.wiz-section { border:1px solid #e5e7eb; border-radius:8px; margin-bottom:1rem; overflow:hidden; }
.wiz-section-header { display:flex; align-items:center; gap:.5rem; padding:.7rem 1rem; background:#f8fafc; border-bottom:1px solid #e5e7eb; font-size:.875rem; font-weight:600; color:#0A354E; i { color:#0A354E; font-size:1rem; } }
.wiz-section-header-detalles { display:flex; align-items:center; justify-content:space-between; padding:.75rem 1rem; background:#f8fafc; border:1px solid #e5e7eb; border-radius:8px; margin-bottom:.75rem; font-size:.875rem; font-weight:600; color:#0A354E; }
.wiz-toggle-row { display:flex; align-items:center; justify-content:space-between; padding:.625rem .875rem; background:#f9fafb; border:1px solid #e5e7eb; border-radius:6px; height:100%; }
.wiz-toggle-label { font-size:.85rem; font-weight:600; color:#1e293b; }
.wiz-toggle-sub   { font-size:.72rem; color:#94a3b8; font-family:monospace; }

.wizard-nav-row { display:flex; justify-content:space-between; align-items:center; padding:1rem 0 .25rem; border-top:1px solid #f1f5f9; margin-top:.5rem; }

.detalle-tab-header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  min-height: 48px;
  flex-wrap: nowrap;
}

.detalle-badge {
  min-width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #0A354E;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;

  &.error {
    background: #ef4444;
    animation: pulse-error 1.5s infinite;
  }
}

@keyframes pulse-error {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.65; }
}

.detalle-header-info {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 1;
  min-width: 0;
}

.detalle-header-top {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.detalle-cliente {
  font-size: 0.875rem;
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}

.detalle-producto-pill {
  display: inline-flex;
  align-items: center;
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  border-radius: 20px;
  padding: 1px 8px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.detalle-header-bottom {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.detalle-ruta-item {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.75rem;
  color: #475569;
  font-weight: 500;
}

.detalle-ciudad-origen {
  color: #065f46;
  font-weight: 600;
}

.detalle-ciudad-destino {
  color: #991b1b;
  font-weight: 600;
}

.detalle-meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 0.7rem;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
}

.detalle-header-geo {
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
}

.geo-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  padding: 2px 7px;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: default;

  &.verde {
    background: #d1fae5;
    color: #065f46;
    border: 1px solid #6ee7b7;
  }

  &.naranja {
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fcd34d;
  }
}

.detalle-btn-eliminar {
  flex-shrink: 0;
  margin-left: auto;
}
.detalle-tab-title { font-weight:600; font-size:.9rem; color:#1e293b; }
.detalle-tab-sub   { color:#64748b; font-weight:400; }
.detalle-ruta      { font-size:.78rem; color:#94a3b8; }


.geocerca-card { border:1px solid #e2e8f0; border-radius:8px; overflow:hidden; margin-top:.25rem; &.verde { border-color:#a7f3d0; } &.rojo { border-color:#fecaca; } }
.geocerca-card-header { display:flex; align-items:center; gap:.5rem; padding:.625rem 1rem; background:#f8fafb; border-bottom:1px solid #e2e8f0; font-size:.82rem; font-weight:600; color:#1e293b; }
.geocerca-badge { margin-left:auto; font-size:.7rem; font-family:monospace; color:#94a3b8; background:#f1f5f9; border:1px solid #e2e8f0; border-radius:4px; padding:1px 6px; }
.geocerca-selector { display:grid; grid-template-columns:1fr 1fr 1fr; gap:.5rem; padding:.75rem 1rem; }
.geocerca-option { border:1.5px solid #e2e8f0; border-radius:6px; padding:.5rem; cursor:pointer; text-align:center; font-size:.78rem; font-weight:500; color:#64748b; background:#fafbfd; transition:all .15s; user-select:none; &:hover { border-color:#0A354E; color:#0A354E; } &.selected { border-color:#0A354E; background:rgba(10,53,78,.06); color:#0A354E; font-weight:700; } .geo-icon { font-size:1rem; display:block; margin-bottom:.15rem; } }
.geocerca-mapa-wrap { padding:.75rem 1rem 1rem; }
.geo-search-row { display:flex; gap:.5rem; align-items:center; margin-bottom:.25rem; }
.geo-search-input { flex:1; font-size:.85rem !important; }
.geo-map { border:1px solid #e2e8f0; }
.geo-controles-circular, .geo-controles-poly { margin-top:.75rem; padding:.75rem; background:#f8fafb; border:1px solid #e2e8f0; border-radius:6px; }
.geo-coord-input { font-family:monospace !important; font-size:.82rem !important; background:#fff !important; cursor:default; }
.geo-slider-row { display:flex; align-items:center; gap:.5rem; margin-top:.5rem; }
.geo-slider-min, .geo-slider-max { font-size:.72rem; color:#94a3b8; white-space:nowrap; }
.geo-slider-badge { min-width:64px; text-align:center; font-family:monospace; font-size:.78rem; font-weight:600; color:#0A354E; background:rgba(10,53,78,.07); border:1px solid rgba(10,53,78,.15); border-radius:4px; padding:2px 6px; white-space:nowrap; }
.geo-poly-table { font-size:.82rem !important; }
.geo-poly-num { width:20px; height:20px; border-radius:50%; background:#0A354E; color:white; font-size:.7rem; font-weight:700; display:inline-flex; align-items:center; justify-content:center; }
.geo-poly-count { display:block; font-size:.78rem; color:#64748b; margin-top:.5rem; }

:deep(.detalles-wizard-accordion) {
  .p-accordion-tab { border:1px solid #e2e8f0; border-radius:8px; margin-bottom:.75rem; overflow:hidden; }
  .p-accordion-content { padding:1rem 1.25rem; }
}
// Variantes de color para headers de sección en detalles
.wiz-section-header--verde {
  color: #065f46;
  i { color: #059669; }
}

.wiz-section-header--rojo {
  color: #991b1b;
  i { color: #E53935; }
}
.vehiculo-buscando {
  display: flex;
  align-items: center;
  padding: 0.625rem 1rem;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #0369a1;
  font-weight: 500;
}

.vehiculo-encontrado-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  gap: 1rem;
}

.vehiculo-encontrado-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.vehiculo-encontrado-titulo {
  font-size: 0.875rem;
  font-weight: 700;
  color: #065f46;
}

.vehiculo-encontrado-sub {
  font-size: 0.8rem;
  color: #047857;
  margin-top: 0.1rem;
}
</style>